import React, { useEffect, useState } from 'react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, UserPlus, LogOut, Inbox, Shield, ChevronDown, Video } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import MyOffersSheet from './MyOffersSheet';
import { useNavigate } from 'react-router-dom';

export default function UserMenu() {
  const { user, isAuthed, isAdmin, openAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [signUp, setSignUp] = useState(false);

  // Flip the CTA between Sign in / Sign up every 1.6s so first-time
  // visitors notice they can create an account too.
  useEffect(() => {
    if (isAuthed) return undefined;
    const t = setInterval(() => setSignUp((v) => !v), 1600);
    return () => clearInterval(t);
  }, [isAuthed]);

  if (!isAuthed) {
    const label = signUp ? 'Sign up' : 'Sign in';
    const Icon = signUp ? UserPlus : User;
    return (
      <button
        data-testid="signin-btn"
        onClick={() => openAuth(signUp ? 'register' : 'login')}
        className="h-10 px-4 rounded-xl bg-[#ff2bd6] hover:bg-[#e119bf] border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] text-white font-black text-sm flex items-center gap-2 transition-all hover:-translate-y-0.5 signin-flash"
      >
        <Icon
          key={`${signUp}-icon`}
          className="h-4 w-4 signin-swap-icon"
        />
        <span
          key={`${signUp}-label`}
          className="signin-swap-label"
        >
          {label}
        </span>
        <style>{`
          @keyframes svSigninSwap {
            0%   { opacity: 0; transform: translateY(4px); }
            30%  { opacity: 1; transform: translateY(0); }
            80%  { opacity: 1; transform: translateY(0); }
            100% { opacity: 0.85; transform: translateY(0); }
          }
          @keyframes svSigninPulse {
            0%, 100% { box-shadow: 0 3px 0 #3a1064, 0 0 0 0 rgba(255, 43, 214, 0.55); }
            50%      { box-shadow: 0 3px 0 #3a1064, 0 0 0 10px rgba(255, 43, 214, 0); }
          }
          .signin-flash { animation: svSigninPulse 1.6s ease-out infinite; }
          .signin-swap-label,
          .signin-swap-icon { animation: svSigninSwap 480ms ease-out; }
          @media (prefers-reduced-motion: reduce) {
            .signin-flash, .signin-swap-label, .signin-swap-icon { animation: none; }
          }
        `}</style>
      </button>
    );
  }

  const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="h-10 pl-1 pr-3 rounded-xl bg-white hover:bg-yellow-50 border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] text-[#3a1064] font-black text-sm flex items-center gap-2 transition-all"
        >
          <span
            className="h-7 w-7 rounded-lg bg-[#ff2bd6] text-white flex items-center justify-center text-sm border-2 border-[#3a1064] font-black overflow-hidden"
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-full w-full object-cover" />
            ) : (
              initial
            )}
          </span>
          <span className="truncate max-w-[120px]">{user?.name || user?.email}</span>
          {isAdmin && (
            <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded bg-[#ffd900] text-[#3a1064] border border-[#3a1064]">
              Admin
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid="user-menu-content"
        className="bg-[#fff7d6] border-2 border-[#3a1064] shadow-[0_4px_0_#3a1064] rounded-xl min-w-[200px]"
      >
        <DropdownMenuLabel className="font-black text-[#3a1064] text-xs uppercase tracking-wider">
          {user?.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#3a1064]/20" />

        <MyOffersSheet
          trigger={
            <DropdownMenuItem
              data-testid="menu-my-offers"
              onSelect={(e) => e.preventDefault()}
              className="font-bold text-[#3a1064] focus:bg-yellow-100 cursor-pointer gap-2"
            >
              <Inbox className="h-4 w-4" /> My offers
            </DropdownMenuItem>
          }
        />

        <DropdownMenuItem
          data-testid="menu-my-profile"
          onClick={() => navigate('/profile')}
          className="font-bold text-[#3a1064] focus:bg-yellow-100 cursor-pointer gap-2"
        >
          <User className="h-4 w-4" /> My profile
        </DropdownMenuItem>

        <DropdownMenuItem
          data-testid="menu-creator-rewards"
          onClick={() => navigate('/profile#creators')}
          className="font-bold text-[#3a1064] focus:bg-yellow-100 cursor-pointer gap-2"
        >
          <Video className="h-4 w-4 text-[#ff2bd6]" /> Creator rewards
        </DropdownMenuItem>

        {isAdmin && (
          <DropdownMenuItem
            data-testid="menu-admin-panel"
            onClick={() => navigate('/admin')}
            className="font-bold text-[#3a1064] focus:bg-yellow-100 cursor-pointer gap-2"
          >
            <Shield className="h-4 w-4" /> Admin panel
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className="bg-[#3a1064]/20" />

        <DropdownMenuItem
          data-testid="menu-logout"
          onClick={logout}
          className="font-bold text-rose-700 focus:bg-rose-50 cursor-pointer gap-2"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
